export interface SlideshowFeatures {
  horizontal: boolean;
  vertical: boolean;
  branded: boolean;
  unbranded: boolean;
  cap?: number;
  capUnbranded?: number;
  /**
   * Ask Bleenk which photos are worth a slide, and in what order,
   * before the first render. Off by default: it changes what every
   * slideshow on the tenant looks like, so it is opted into per tenant
   * rather than switched on globally. See
   * `~/.server/slideshow-auto-select`.
   */
  autoSelect?: boolean;
  /**
   * The videos came with a package the order bought (MCM's Sky-to-Door
   * Tour), not a Slideshow line: they count as ordered all the same, so
   * the customer is never told to add one.
   */
  inPackage?: boolean;
}

export type SlideshowOrientation = 'landscape' | 'portrait';

export interface SlideshowVariant {
  /** Agent profile the video is branded for; null = unbranded. */
  profileId: string | null;
  orientation: SlideshowOrientation;
}

/**
 * Orders created on/after this instant follow the rules below: midnight
 * UTC on 2026-09-20, which is the evening of the 19th in US time zones.
 * Earlier orders keep the old rules, so a rule change never leaves a
 * delivered video deleted-and-not-rebuilt on the next re-sync.
 */
export const SLIDESHOW_VARIANTS_CUTOFF_MS = Date.UTC(2026, 8, 20);

/**
 * Orders created on/after this instant get no unbranded 9:16: midnight UTC
 * on 2026-10-03, which is the evening of the 2nd in US time zones (8 PM
 * Eastern), the same convention as the cutoff above.
 *
 * The founder's rule, 2 Oct 2026: "we do not need an unbranded 9:16. the
 * only unbranded things are items that can go on the unbranded property
 * page", and "cut off date Oct 3rd". The property page plays an unbranded
 * 16:9 and never a 9:16 (micrositeSlideshowSkip), so a vertical video with
 * no agent on it has nowhere to go: the 9:16 is for an agent's social feed,
 * and is branded.
 *
 * A date, like the others here, so that nothing delivered is taken away:
 * an earlier order keeps the videos it was built with, the unbranded 9:16
 * among them, and keeps them in sync. It is the date the ORDER was placed,
 * not the date this shipped; see `keepUnbrandedPortrait` for the orders in
 * between.
 */
export const SLIDESHOW_NO_UNBRANDED_PORTRAIT_CUTOFF_MS = Date.UTC(2026, 9, 3);

/**
 * Orders created on/after this instant get a slide cap: midnight UTC on
 * 2026-09-22, the evening of the 21st in US time zones.
 *
 * LIVE ON PRODUCTION SINCE 2026-09-20 (#332): NEVER MOVE IT. The cap
 * changes `input_images`, and `clean_slideshow` deletes and re-renders any
 * automated slideshow whose images no longer match. Moving this date
 * earlier would shorten videos already delivered; moving it later would
 * lengthen every capped video delivered since the 22nd.
 */
export const SLIDESHOW_CAP_CUTOFF_MS = Date.UTC(2026, 8, 22);

/**
 * Orders created on/after this instant get today's slideshow: photos chosen
 * by auto-selection, in the current design (3.8-second slides, the
 * neighbourhood-led opening). 1 PM Eastern on 2026-09-26: the founder wanted
 * it "available starting today".
 *
 * A DATE THAT PRECEDES THE DEPLOY RE-CUTS DELIVERED VIDEOS. Selection
 * changes `input_images`, which re-renders the video, and the microsite
 * player draws the design live. This one was checked against production at
 * 21:45 UTC that day: the only order placed since midnight Eastern (#16239,
 * 16:16 UTC, 8 photos, video already rendered) falls before it, and none
 * after. Moving it earlier needs the same check. It is never earlier than the
 * cap cutoff: every order on the current design is capped.
 */
export const SLIDESHOW_DESIGN_CUTOFF_MS = Date.UTC(2026, 8, 26, 17);

/**
 * Slides an automated slideshow gets when the tenant names no number of
 * its own. Twenty at 5 seconds is about 1m54s with the map slide and the
 * end card.
 */
export const SLIDESHOW_DEFAULT_CAP = 20;

/**
 * Whether an order gets today's slideshow behaviour or the one it was
 * delivered under.
 *
 * ONE question, asked for both auto-selection and the design version,
 * because a half-answer is worse than either: an order that gets chosen
 * photos but the legacy timing. The cap has its own, earlier date (it
 * shipped first), which is safe in one direction only: every order on the
 * current design is capped, never the current look over an uncapped
 * hundred and fifty slides.
 *
 * `forced` is the escape hatch, and it exists for a specific problem:
 * the cutoff is a FUTURE date, so between deploying and reaching it
 * there is no order on production that can exercise any of this. The
 * alternative was moving the cutoff to "now" for the test, which gives
 * up the one thing it is for — a deploy that cannot re-cut a video
 * somebody already downloaded — on the day you most want it.
 *
 * Per ORDER, never global. Setting it says "this one order, on
 * purpose"; the date keeps protecting every other one.
 */
export function slideshowUsesCurrentDesign(orderCreatedMs: number, forced?: boolean): boolean {
  if (forced === true) {
    return true;
  }

  // A timestamp we cannot read is treated as old. Every unknown here
  // should fail towards leaving a delivered video alone.
  return Number.isFinite(orderCreatedMs) && orderCreatedMs >= SLIDESHOW_DESIGN_CUTOFF_MS;
}

/**
 * How many photos this order's slideshow may use.
 *
 * Photology rendered every photo an agent had not hidden — a median of
 * 37 and up to 173 — at roughly 45 seconds of render each. Orders over
 * 100 photos failed three times in four, and the ones that finished ran
 * eight minutes, which nobody watches.
 *
 * The cap lives here rather than in each tenant's `slideshows` getter on
 * purpose. Twist alone has five branches, and the one that matters most
 * — a paid slideshow — is checked first and named no cap at all; MCM has
 * two and names none. A default that every tenant inherits cannot be
 * forgotten by the next branch somebody adds.
 *
 * Which photos get the slides is a separate question with a better
 * answer than "the first twenty". This is the floor: a video that
 * renders, whatever else is or is not working.
 */
export function slideshowImageCap(
  features: SlideshowFeatures | null | undefined,
  orderCreatedMs: number,
  options: { includeHidden?: boolean; forced?: boolean } = {},
): number | undefined {
  // A caller asking for hidden photos as well is building a management
  // view of everything the order holds, not the list that plays.
  if (options.includeHidden) {
    return undefined;
  }

  // No slideshow on this order, so nothing to cap.
  if (!features) {
    return undefined;
  }

  // A tenant's own number always wins. It is explicit, it predates this
  // rule, and it is already baked into videos that have shipped.
  if (features.cap) {
    return features.cap;
  }

  // A timestamp we cannot read is treated as old. Every unknown here
  // should fail towards leaving a delivered video alone.
  const capped = Number.isFinite(orderCreatedMs) && orderCreatedMs >= SLIDESHOW_CAP_CUTOFF_MS;

  if (options.forced !== true && !capped) {
    return undefined;
  }

  return SLIDESHOW_DEFAULT_CAP;
}

/**
 * How many slides the vertical reel gets — always today's number.
 *
 * The reel is the one variant the cutoff does not protect, because the
 * cutoff protects videos that were already DELIVERED. A reel is bought
 * or granted after the fact and has never existed, so there is nothing
 * to re-cut and no reason to build it under the rules of the day the
 * order was taken.
 *
 * Without this it was built from the order's uncapped list: a 149-photo
 * order created before the cutoff produced a 4m14s "Instagram reel"
 * against a panel selling "45 seconds", and the granted reels are on
 * orders of 154 photos (23 Sep 2026).
 *
 * A tenant's own `cap` still wins — `slideshowImageCap` checks it first
 * — so this forces the DATE, not the number.
 */
export function slideshowReelCap(
  features: SlideshowFeatures | null | undefined,
  orderCreatedMs: number,
): number | undefined {
  return slideshowImageCap(features, orderCreatedMs, { forced: true });
}

/**
 * The photos one automated video is built from.
 *
 * Only a BOUGHT or GRANTED reel takes the reel list (capped at today's
 * number whatever the order's date): it is added after delivery and has
 * never existed. A 9:16 that came with the order (Twist's $35 slideshow,
 * MCM's vertical) was delivered from the order's own list and is compared
 * against it on every pass, so handing it the reel list would delete and
 * re-render every one of them over twenty photos (26 Sep 2026 review).
 */
export function slideshowVariantImages(
  variant: { branded: boolean; orientation: SlideshowOrientation },
  lists: { branded: string[]; unbranded: string[]; reel: string[] },
  reelBought: boolean,
): string[] {
  if (variant.orientation === 'portrait' && reelBought) {
    return lists.reel;
  }

  return variant.branded ? lists.branded : lists.unbranded;
}

/**
 * Where a Sky-to-Door waypoint still plays: the first slide AFTER the
 * opening title, not index 0.
 *
 * Index 0 is not a slide of its own in any template. Bubbles (the default)
 * draws it as the title card's backdrop, in GRAYSCALE unless the order
 * turned colour on, under the address; Basic lays the brand intro over it
 * for its first four seconds, which is the whole 3.8-second slide; the 9:16
 * reel puts the same title over the upper pane it opens. The still is
 * landmark names and drive times, so at 0 it would be read by nobody.
 *
 * At 1 it is the first clean slide in Bubbles, and follows the map in Basic
 * and the reel (the map is spliced in after the opener): "where it is",
 * then "what is near it".
 */
export const SLIDESHOW_WAYPOINT_INDEX = 1;

/**
 * The waypoint stills an order holds: one for each video shape, and the 9:16
 * reel's own pieces in play order.
 */
export type SlideshowWaypoints = Partial<Record<SlideshowOrientation, string>> & {
  /**
   * The 9:16 social reel's pieces, 1 to SLIDESHOW_REEL_PIECES. For a reel
   * that already exists the caller passes the pieces THAT reel should hold,
   * which for a bought one are the pieces it was built with (clean_slideshow).
   */
  reel?: string[];
};

/** How many pieces Bleenk cuts the stops into for the reel's lower pane. */
export const SLIDESHOW_REEL_PIECES = 3;

/**
 * Whether an order's 9:16 is the two-pane social reel, the one with a lower
 * pane for the pieces. An unset variant is the social reel, as the render
 * reads it (OrderSlideshowEntity.input). One rule for the render
 * (clean_slideshow) and the offer's preview (reelPanel), which must agree.
 */
export function slideshowIsSocialReel(variant: 'cropped' | 'social_reel' | null | undefined): boolean {
  return (variant ?? 'social_reel') === 'social_reel';
}

/**
 * The address a slideshow's opening title shows: the street line and the
 * unit. With the address as the headline, every unit in a building would
 * otherwise share one title card.
 *
 * One rule for the render (OrderSlideshowEntity.input) and every preview
 * (OrderEntity.slideshowPreview), which must agree. The previews showed the
 * street line alone until 2 Oct 2026, so a condo's preview, and the reel
 * offer's headline, lacked the unit number the video carries.
 */
export function slideshowAddressLine(address: { line1?: string | null; line2?: string | null } | null | undefined): string {
  return [address?.line1, address?.line2].filter(Boolean).join(' ');
}

/**
 * Photos a BRANDED 16:9 keeps when it leads with a waypoint still: one fewer
 * than the cap. Twenty photos, the map and the still are 22 slides x 3.8s
 * plus the 9s end card = 92.6s, past the 90s ceiling SLIDESHOW_SLIDE is
 * sized for; 19 + 1 is 88.8s again (founder, 30 Sep 2026). The unbranded
 * 16:9 has no end card (20 + 1 + map = 83.6s) and keeps all twenty.
 */
export const SLIDESHOW_WAYPOINT_BRANDED_PHOTOS = SLIDESHOW_DEFAULT_CAP - 1;

/**
 * A 9:16 social reel's list with its waypoint pieces in the LOWER pane, as
 * consecutive slides from slide 2.
 *
 * The reel deals its list between two panes by position: even indices to
 * the upper pane, odd to the lower, which opens on the map
 * (slideshow/src/compositions/social-reel/lib/Reels.tsx). So the pieces go
 * at the odd indices 1, 3 and 5, each after one photo:
 *
 *   list   p0 R1 p1 R2 p2 R3 p3 p4 p5 ...
 *   upper  p0    p1    p2    p3    p5 ...
 *   lower  map R1    R2    R3    p4    ...
 *
 * The same index as the 16:9's still (SLIDESHOW_WAYPOINT_INDEX), and the
 * same place in the story: the map, then what is near it. The photos keep
 * their order and none is dropped: twenty photos, three pieces and the map
 * are 24 items, 12 to a pane (43.1s before the end card).
 *
 * It takes one photo more than there are pieces. With fewer there is no
 * photo to put between them, and with exactly as many the list ends on a
 * piece, which the reel drops when it trims an odd list to balance its panes
 * (reelBalancedAssets): two pieces of three. Such a reel plays none.
 */
export function slideshowWithReelPieces(images: string[], pieces: string[]): string[] {
  const photos = images.filter((image) => !pieces.includes(image));

  if (pieces.length === 0 || photos.length <= pieces.length) {
    return photos.length === images.length ? images : photos;
  }

  return [...pieces.flatMap((piece, i) => [photos[i], piece]), ...photos.slice(pieces.length)];
}

/**
 * One automated video's photo list with the Sky-to-Door waypoint stills
 * added, `waypoints` holding them only once the order is known to have
 * bought Sky-to-Door (orderWaypointSlides).
 *
 * 16:9. The unbranded video is the photos exactly as today plus the still
 * (20 + 1). The branded one keeps the FIRST 19 of the same list, so the
 * still costs the last photo and nothing is reshuffled.
 *
 * 9:16. Never the 9:16 still: the social reel shows an image in a pane about
 * 972x839, so that still would lose half its height (founder, 30 Sep 2026:
 * "three slices, not one cropped still"). A SOCIAL REEL takes the reel
 * pieces instead, cut to that pane (slideshowWithReelPieces). The cropped
 * 9:16 has no pane and takes nothing.
 *
 * A bought reel is the one video that texts the agent on every render
 * (reel-delivery.ts), so its list must never change once it is built: the
 * caller hands an existing one the pieces it already holds, not the order's
 * current ones. A reel that does not exist yet is not built without its
 * pieces at all on a Sky-to-Door order (clean_slideshow, reel-hold.ts).
 *
 * clean_slideshow compares every video against this and builds missing ones
 * from it, so it must be a pure function of its inputs.
 */
export function slideshowWithWaypoint(
  images: string[],
  variant: { orientation: SlideshowOrientation; branded: boolean; socialReel?: boolean },
  waypoints: SlideshowWaypoints,
): string[] {
  if (variant.orientation === 'portrait') {
    return variant.socialReel ? slideshowWithReelPieces(images, waypoints.reel ?? []) : images;
  }

  const waypoint = waypoints.landscape;

  if (!waypoint || images.length === 0) {
    return images;
  }

  const all = images.filter((image) => image !== waypoint);
  const photos = variant.branded ? all.slice(0, SLIDESHOW_WAYPOINT_BRANDED_PHOTOS) : all;

  return [...photos.slice(0, SLIDESHOW_WAYPOINT_INDEX), waypoint, ...photos.slice(SLIDESHOW_WAYPOINT_INDEX)];
}

/**
 * Which automated slideshow videos an order should have.
 *
 * The unbranded 9:16 (portrait), by the order's date:
 *
 *  - From 3 Oct 2026 (SLIDESHOW_NO_UNBRANDED_PORTRAIT_CUTOFF_MS): never. The
 *    9:16 is branded or it is not made. A customer with no agent profile
 *    gets none until they add one, and an order that bought only the
 *    vertical has no unbranded video at all.
 *  - From 20 Sep 2026: not where the agent's branded 9:16 covers it. It
 *    survives in two cases, where dropping it would leave the order with
 *    nothing usable rather than one video fewer: nothing is branded (no
 *    agent profile), so there would be no 9:16 at all; or there is no
 *    unbranded 16:9 either (a portrait-only purchase), so the order would
 *    have nothing to put where agent branding is not allowed.
 *  - Before that: always, beside the branded one.
 *
 * `keepUnbrandedPortrait` sets the 3 Oct rule aside for an order that
 * already has that video, or has paid for a reel under the rules before it:
 * the pass (clean_slideshow) says so, and the order is then treated as one
 * placed the day before. The rule stops new ones being made; it never takes
 * one away, and never leaves a paid reel unbuilt. Nothing that decides what
 * to SELL passes it (reel-deliverable.ts).
 */
export function slideshowVariants(
  features: SlideshowFeatures,
  profileIds: string[],
  orderCreatedMs: number,
  options: { keepUnbrandedPortrait?: boolean } = {},
): SlideshowVariant[] {
  const out: SlideshowVariant[] = [];

  const brandedPortrait = features.branded && profileIds.length > 0;
  const unbrandedLandscape = features.unbranded && features.horizontal;
  const dropUnbrandedPortrait =
    orderCreatedMs >= SLIDESHOW_VARIANTS_CUTOFF_MS && brandedPortrait && unbrandedLandscape;
  // An unreadable timestamp compares false, and so counts as old: every
  // unknown here fails towards leaving a delivered video alone.
  const noUnbrandedPortrait =
    orderCreatedMs >= SLIDESHOW_NO_UNBRANDED_PORTRAIT_CUTOFF_MS && !options.keepUnbrandedPortrait;

  if (features.unbranded) {
    if (features.horizontal) {
      out.push({ profileId: null, orientation: 'landscape' });
    }

    if (features.vertical && !dropUnbrandedPortrait && !noUnbrandedPortrait) {
      out.push({ profileId: null, orientation: 'portrait' });
    }
  }

  if (features.branded) {
    for (const profileId of profileIds) {
      if (features.horizontal) {
        out.push({ profileId, orientation: 'landscape' });
      }

      if (features.vertical) {
        out.push({ profileId, orientation: 'portrait' });
      }
    }
  }

  return out;
}

/**
 * Whether an order makes an unbranded video at all: what a customer with no
 * agent profile has on its way. Since 3 Oct 2026 that is the unbranded 16:9
 * and nothing else, so an order that bought only the vertical makes none.
 */
export function slideshowMakesUnbranded(
  features: SlideshowFeatures | null | undefined,
  orderCreatedMs: number,
): boolean {
  return Boolean(features) && slideshowVariants(features as SlideshowFeatures, [], orderCreatedMs).length > 0;
}

/**
 * The agent profiles that get branded videos: archived profiles are
 * skipped, since each one costs a full extra render — unless every profile
 * is archived, in which case they are kept so the order still gets one.
 *
 * `withExistingVideo` exempts profiles the order already has a video for.
 * The caller deletes and rebuilds videos whose photos or style drifted, so
 * without this an archived profile's delivered video would be deleted on
 * the next re-sync and never rebuilt.
 */
export function slideshowProfileIds(
  profiles: Array<{ id: string; archived: boolean }>,
  withExistingVideo: Iterable<string> = [],
): string[] {
  const keep = new Set(withExistingVideo);
  const wanted = profiles.filter((profile) => !profile.archived || keep.has(profile.id));

  return (wanted.length > 0 ? wanted : profiles).map((profile) => profile.id);
}
